import { ref, computed, watch } from "vue";
import { defineStore } from "pinia";
import { v4 as uuidv4 } from "uuid";
import {
  ref as storageRef,
  uploadBytesResumable,
  type UploadTask,
} from "firebase/storage";
import { firebaseStorage } from "@/helpers/firebase/firebase";
import { useShortsStore } from "@/stores/modules/shorts/shorts";
import { useUserStore } from "@/stores/modules/user/user";
import { useQuotaStore } from "@/stores/modules/quota/quota";
import { generateTrigrams } from "@/helpers/utils/trigrams";
import { DEFAULT_SHORT_MAX_DURATION_SECONDS } from "@/constants/shorts";
import type { ShortDocument } from "@kekkle/shared/types/documents/short-document/short-document";
import {
  saveUploadJob,
  deleteUploadJob,
  getAllUploadJobs,
  updateUploadJobStatus,
  type PersistedShortUpload,
} from "@/helpers/cache/short-upload-queue";
import {
  extractShortPosterAndMetadata,
  type ShortExtractedMetadata,
} from "@/helpers/shorts/short-metadata";
import { deleteShortMedia } from "@/helpers/firestorage/ShortMediaService";
import { setSuccessToast, setErrorToast } from "@/helpers/notifications/toast";
import { translate } from "@/i18n/vueI18n";

export type ShortUploadItem = {
  id: string;
  groupId: string;
  authUuid: string;
  caption: string;
  duration: number;
  blurhash?: string;
  posterUrl: string;
  posterBlob?: Blob;
  videoBlob: Blob;
  progress: number;
  status: "preparing" | "uploading" | "failed" | "completed";
  errorMessage?: string;
  createdAt: number;
  cancelTask?: () => void;
};

export type StartUploadOptions = {
  file: File | Blob;
  caption: string;
  groupId: string;
  authUuid: string;
  precomputedMetadataPromise?: Promise<ShortExtractedMetadata>;
};

export const useShortUploadStore = defineStore("shortUpload", () => {
  const shortsStore = useShortsStore();
  const userStore = useUserStore();
  const quotaStore = useQuotaStore();

  const activeUploads = ref<Record<string, ShortUploadItem>>({});

  const activeGroupId = computed(() => userStore.activeGroup);

  /** Active or failed upload for the currently active group */
  const currentGroupUpload = computed<ShortUploadItem | null>(() => {
    if (!activeGroupId.value) return null;
    const match = Object.values(activeUploads.value).find(
      (item) => item.groupId === activeGroupId.value,
    );
    return match || null;
  });

  /** Whether any upload is actively transferring across the app */
  const isUploading = computed<boolean>(() => {
    return Object.values(activeUploads.value).some(
      (item) => item.status === "preparing" || item.status === "uploading",
    );
  });

  /** Whether an upload is actively transferring for the active group */
  const isUploadingForCurrentGroup = computed<boolean>(() => {
    return (
      currentGroupUpload.value?.status === "preparing" ||
      currentGroupUpload.value?.status === "uploading"
    );
  });

  // Browser navigation safety warning while an upload is active
  function handleBeforeUnload(e: BeforeUnloadEvent): string {
    e.preventDefault();
    e.returnValue = "";
    return "";
  }

  watch(isUploading, (uploading) => {
    if (typeof window === "undefined") return;
    if (uploading) {
      window.addEventListener("beforeunload", handleBeforeUnload);
    } else {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    }
  });

  // Watch for newly arrived short documents in Firestore to dismiss completed placeholder
  watch(
    () => shortsStore.allShorts,
    (currentShorts) => {
      if (!currentShorts || currentShorts.length === 0) return;
      for (const [id, upload] of Object.entries(activeUploads.value)) {
        if (upload.status === "completed") {
          const match = currentShorts.some(
            (s) =>
              s.video_url === `shorts/${id}.mp4` ||
              s.absolute_video_url?.includes(id),
          );
          if (match) {
            setTimeout(() => {
              cleanupUploadItem(id);
            }, 800);
          }
        }
      }
    },
    { deep: true },
  );

  function cleanupUploadItem(id: string): void {
    const item = activeUploads.value[id];
    if (item?.posterUrl && item.posterUrl.startsWith("blob:")) {
      URL.revokeObjectURL(item.posterUrl);
    }
    delete activeUploads.value[id];
  }

  function updateUploadItem(id: string, patch: Partial<ShortUploadItem>): void {
    const item = activeUploads.value[id];
    if (item) {
      Object.assign(item, patch);
    }
  }

  async function executeUploadPipeline(
    id: string,
    precomputedMetadataPromise?: Promise<ShortExtractedMetadata>,
  ): Promise<void> {
    const initialItem = activeUploads.value[id];
    if (!initialItem) return;

    const { groupId, authUuid, caption, videoBlob } = initialItem;
    const posterStoragePath = `groups/${groupId}/shorts/${id}_poster.jpg`;
    const videoStoragePath = `groups/${groupId}/shorts/${id}.mp4`;

    // discardUpload removes the item from state; every await is a point where that may have happened
    const isDiscarded = () => !activeUploads.value[id];
    const removeOrphanedMedia = () =>
      deleteShortMedia([posterStoragePath, videoStoragePath]);

    try {
      // 1. Ensure poster, duration, and blurhash are available
      if (!initialItem.posterBlob || !initialItem.duration) {
        updateUploadItem(id, { status: "preparing", progress: 8 });
        let metadata: ShortExtractedMetadata;
        if (precomputedMetadataPromise) {
          try {
            metadata = await precomputedMetadataPromise;
          } catch {
            metadata = await extractShortPosterAndMetadata(videoBlob);
          }
        } else {
          metadata = await extractShortPosterAndMetadata(videoBlob);
        }
        if (isDiscarded()) {
          metadata.cleanup();
          return;
        }
        updateUploadItem(id, {
          posterBlob: metadata.posterBlob,
          posterUrl: metadata.posterUrl,
          blurhash: metadata.blurhash,
          duration: metadata.duration,
        });
      }

      updateUploadItem(id, { status: "uploading", progress: 15 });

      // 2. Persist to IndexedDB so the upload can be recovered if app is closed
      const currentItem = activeUploads.value[id] || initialItem;
      const persistedJob: PersistedShortUpload = {
        id,
        groupId,
        authUuid,
        caption,
        duration: currentItem.duration,
        blurhash: currentItem.blurhash,
        videoBlob,
        posterBlob: currentItem.posterBlob,
        createdAt: currentItem.createdAt,
        status: "uploading",
      };
      await saveUploadJob(persistedJob).catch((e) => {
        console.warn("Failed to persist upload job to IndexedDB:", e);
      });
      if (isDiscarded()) {
        // Discarded while persisting: undo the save so the job isn't restored on next launch
        await deleteUploadJob(id).catch(() => {});
        return;
      }

      // 3. Upload poster to Firebase Storage
      const posterStorageReference = storageRef(
        firebaseStorage,
        posterStoragePath,
      );

      const posterBlobToUpload =
        activeUploads.value[id]?.posterBlob || initialItem.posterBlob;
      let posterTask: UploadTask | null = null;
      if (posterBlobToUpload) {
        posterTask = uploadBytesResumable(
          posterStorageReference,
          posterBlobToUpload,
          {
            contentType: "image/jpeg",
            customMetadata: { uploadedBy: authUuid },
          },
        );
        updateUploadItem(id, { cancelTask: () => posterTask?.cancel() });
        await posterTask;
      }
      if (isDiscarded()) {
        await removeOrphanedMedia();
        return;
      }

      updateUploadItem(id, { progress: 25 });

      // 4. Upload video to Firebase Storage with progress tracking
      const videoStorageReference = storageRef(
        firebaseStorage,
        videoStoragePath,
      );

      const videoTask = uploadBytesResumable(videoStorageReference, videoBlob, {
        contentType: "video/mp4",
        customMetadata: { uploadedBy: authUuid },
      });

      updateUploadItem(id, { cancelTask: () => videoTask.cancel() });

      await new Promise<void>((resolve, reject) => {
        videoTask.on(
          "state_changed",
          (snapshot) => {
            if (snapshot.totalBytes > 0) {
              const pct =
                (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              // Map video transfer to 25% -> 90%
              const mappedProgress = Math.max(
                25,
                Math.min(Math.round(25 + pct * 0.65), 90),
              );
              updateUploadItem(id, { progress: mappedProgress });
            }
          },
          (error) => reject(error),
          () => resolve(),
        );
      });
      if (isDiscarded()) {
        await removeOrphanedMedia();
        return;
      }

      updateUploadItem(id, { progress: 92 });

      // 5. Create ShortDocument in Firestore
      const cleanCaption = caption.trim();
      const finalItem = activeUploads.value[id] || initialItem;
      const shortData: Omit<ShortDocument, "_id"> = {
        absolute_video_url: videoStoragePath,
        video_url: `shorts/${id}.mp4`,
        absolute_thumbnail_url: posterStoragePath,
        thumbnail_url: `shorts/${id}_poster.jpg`,
        duration: Math.min(
          Math.max(Math.round(finalItem.duration * 10) / 10, 0.5),
          DEFAULT_SHORT_MAX_DURATION_SECONDS,
        ),
        caption: cleanCaption,
        trigrams: generateTrigrams(cleanCaption),
        created_by: authUuid,
        timestamp: new Date(),
        created_at: new Date(),
        updated_at: new Date(),
        number_of_comments: 0,
        reported_by: [],
      };
      if (finalItem.blurhash) {
        shortData.blurhash = finalItem.blurhash;
      }

      await shortsStore.addShort(groupId, shortData);

      updateUploadItem(id, { progress: 100, status: "completed" });

      // 6. Clean up IndexedDB & notify
      await deleteUploadJob(id).catch(() => {});
      quotaStore.fetchQuota().catch(() => {});

      setSuccessToast({
        message: translate("pages.shorts.posted_success"),
        emoji: "🚀",
      });

      // Fallback cleanup in case Firestore snapshot already fired or is delayed
      setTimeout(() => {
        cleanupUploadItem(id);
      }, 1000);
    } catch (err: any) {
      if (isDiscarded()) {
        // Cancelled mid-transfer: the poster may already be uploaded
        await removeOrphanedMedia();
        return;
      }
      if (err?.code === "storage/canceled") {
        return;
      }
      console.error("Short upload failed:", err);
      const errorMessage =
        err?.message || translate("pages.shorts.upload_failed");
      updateUploadItem(id, {
        status: "failed",
        errorMessage,
      });

      await updateUploadJobStatus(id, "failed", errorMessage).catch(() => {});

      setErrorToast({
        message: translate("pages.shorts.upload_failed"),
      });
    } finally {
      if (activeUploads.value[id]) {
        activeUploads.value[id].cancelTask = undefined;
      }
    }
  }

  /**
   * Starts a new short background upload.
   * Immediately registers the upload in state and begins uploading without blocking the caller.
   */
  async function startUpload(options: StartUploadOptions): Promise<string> {
    const fileId = uuidv4();

    const uploadItem: ShortUploadItem = {
      id: fileId,
      groupId: options.groupId,
      authUuid: options.authUuid,
      caption: options.caption,
      duration: DEFAULT_SHORT_MAX_DURATION_SECONDS,
      posterUrl: "",
      videoBlob: options.file,
      progress: 5,
      status: "uploading",
      createdAt: Date.now(),
    };

    activeUploads.value[fileId] = uploadItem;

    // Attach precomputed metadata when available
    if (options.precomputedMetadataPromise) {
      options.precomputedMetadataPromise
        .then((meta) => {
          updateUploadItem(fileId, {
            posterBlob: meta.posterBlob,
            posterUrl: meta.posterUrl,
            blurhash: meta.blurhash,
            duration: meta.duration,
          });
        })
        .catch(() => {});
    }

    // Run upload pipeline asynchronously in the background
    executeUploadPipeline(fileId, options.precomputedMetadataPromise);

    return fileId;
  }

  /**
   * Retries an upload that failed or was interrupted.
   */
  async function retryUpload(id: string): Promise<void> {
    const item = activeUploads.value[id];
    if (!item) return;

    updateUploadItem(id, {
      status: "uploading",
      errorMessage: undefined,
      progress: 5,
    });

    await executeUploadPipeline(id);
  }

  /**
   * Discards an upload and cleans up local and persistent storage.
   */
  async function discardUpload(id: string): Promise<void> {
    const item = activeUploads.value[id];
    if (item?.cancelTask) {
      item.cancelTask();
    }
    cleanupUploadItem(id);
    await deleteUploadJob(id).catch(() => {});
  }

  /**
   * Restores any unfinished upload jobs persisted in IndexedDB.
   * Auto-resumes if the device is currently online.
   */
  async function restorePendingUploads(): Promise<void> {
    try {
      const persisted = await getAllUploadJobs();
      if (!persisted || persisted.length === 0) return;

      for (const job of persisted) {
        if (!activeUploads.value[job.id]) {
          const posterUrl = job.posterBlob
            ? URL.createObjectURL(job.posterBlob)
            : "";

          const uploadItem: ShortUploadItem = {
            id: job.id,
            groupId: job.groupId,
            authUuid: job.authUuid,
            caption: job.caption,
            duration: job.duration,
            blurhash: job.blurhash,
            posterUrl,
            posterBlob: job.posterBlob,
            videoBlob: job.videoBlob,
            progress: 0,
            status: "failed",
            errorMessage:
              job.errorMessage || translate("pages.shorts.upload_interrupted"),
            createdAt: job.createdAt,
          };

          activeUploads.value[job.id] = uploadItem;

          // Auto-resume if online
          const isOnline =
            typeof navigator !== "undefined" ? navigator.onLine : true;
          if (isOnline) {
            retryUpload(job.id).catch(() => {});
          }
        }
      }
    } catch (e) {
      console.error("Error restoring pending uploads from IndexedDB:", e);
    }
  }

  /**
   * Resets all upload store state (e.g. on logout)
   */
  function reset(): void {
    if (typeof window !== "undefined") {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    }
    for (const [id, item] of Object.entries(activeUploads.value)) {
      if (item.cancelTask) {
        item.cancelTask();
      }
      if (item.posterUrl && item.posterUrl.startsWith("blob:")) {
        URL.revokeObjectURL(item.posterUrl);
      }
      delete activeUploads.value[id];
    }
    activeUploads.value = {};
  }

  return {
    activeUploads,
    currentGroupUpload,
    isUploading,
    isUploadingForCurrentGroup,
    startUpload,
    retryUpload,
    discardUpload,
    restorePendingUploads,
    reset,
  };
});
